import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { getColorHex, isLightColor } from '../utils/colorMap';
import { resolveImageUrl } from '../utils/imageUrl';
import Footer from '../components/Footer';
import TopBar from '../components/TopBar';

const WishlistPage = () => {
  const navigate = useNavigate();
  const { wishlist, removeFromWishlist } = useWishlist();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-pink-50 flex flex-col">
      <TopBar />
      <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <Button onClick={() => navigate('/')} variant="ghost" className="mb-6" data-testid="wishlist-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-red-400 to-pink-500 mb-4">
            <Heart className="w-8 h-8 text-white" fill="white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="wishlist-title">Mi Lista de Deseos</h1>
          <p className="text-base text-gray-600 mt-2">
            {wishlist.length > 0 ? `${wishlist.length} producto${wishlist.length > 1 ? 's' : ''} guardado${wishlist.length > 1 ? 's' : ''}` : 'Tu lista está vacía'}
          </p>
        </div>

        {wishlist.length === 0 ? (
          <Card className="p-10 text-center max-w-md mx-auto" data-testid="wishlist-empty">
            <Heart className="w-16 h-16 mx-auto text-gray-200 mb-4" />
            <p className="text-gray-500 mb-4">Aún no has guardado ningún producto</p>
            <Button onClick={() => navigate('/mujer')} className="bg-black">
              <ShoppingBag className="w-4 h-4 mr-2" /> Explorar productos
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wishlist.map(item => (
              <Card key={item.id} className="overflow-hidden group hover:shadow-lg transition-shadow" data-testid={`wishlist-item-${item.id}`}>
                <div className="relative">
                  <img
                    src={resolveImageUrl(item.image_url)}
                    alt={item.name}
                    className="w-full h-48 object-cover cursor-pointer"
                    onClick={() => navigate(`/product/${item.id}`)}
                  />
                  <button
                    onClick={() => removeFromWishlist(item.id)}
                    className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow hover:bg-red-50 transition-colors"
                    data-testid={`wishlist-remove-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
                <div className="p-4">
                  <h3
                    className="font-bold text-gray-800 mb-1 cursor-pointer hover:text-black transition-colors"
                    onClick={() => navigate(`/product/${item.id}`)}
                  >
                    {item.name}
                  </h3>
                  <p className="text-lg font-bold text-black mb-2">{item.price?.toFixed(2)}€</p>

                  {/* Colors */}
                  {item.colors && item.colors.length > 0 && (
                    <div className="flex gap-1 mb-3">
                      {item.colors.map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full"
                          style={{
                            backgroundColor: getColorHex(color),
                            border: isLightColor(color) ? '1px solid #d1d5db' : 'none'
                          }}
                          title={color}
                        />
                      ))}
                    </div>
                  )}

                  <Button
                    onClick={() => navigate(`/product/${item.id}`)}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-sm"
                    data-testid={`wishlist-view-${item.id}`}
                  >
                    <ShoppingBag className="w-3 h-3 mr-2" /> Ver producto
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default WishlistPage;
